// src/context/PatientContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import axiosApexInstance from '../auth/axiosApexInstance';
import { useUser } from './UserContext';
import {MISSING_TOKEN_ERROR_MSG} from "../Constants";
import { PatientDTO } from '../types';

interface PatientContextType {
    selectedPatient: PatientDTO | null;
    setSelectedPatient: (patient: PatientDTO | null) => void;
    patientList: PatientDTO[];
    getNewPatients: () => void;
    addPatientRow: (newPatient: PatientDTO) => void;
    refreshPatients: (patientId: number) => void;
    getPatientRow: (patientId: number) => PatientDTO | null;
}

const PatientContext = createContext<PatientContextType | undefined>(undefined);

const SELECTED_PATIENT_KEY = 'apex_selected_patient'; // ✅ Save in localStorage

export const PatientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedPatient, setSelectedPatientState] = useState<PatientDTO | null>(null);
    const [patientList, setPatientList] = useState<PatientDTO[]>([]);
    const { accessToken } = useUser();

    useEffect(() => {
        getNewPatients();
        // Try to restore last selected patient
        const savedPatient = localStorage.getItem(SELECTED_PATIENT_KEY);
        if (savedPatient) {
            try {
                const parsed = JSON.parse(savedPatient);
                setSelectedPatientState(parsed);
            } catch (err) {
                console.error('Failed to restore saved patient:', err);
            }
        }
    }, [accessToken]);

    const setSelectedPatient = (patient: PatientDTO | null) => {
        setSelectedPatientState(patient);
        if (patient) {
            localStorage.setItem(SELECTED_PATIENT_KEY, JSON.stringify(patient));
        } else {
            localStorage.removeItem(SELECTED_PATIENT_KEY);
        }
    };

    const getNewPatients = async () => {
        try {
            const res = await axiosApexInstance.get('/task/patient/all');
            setPatientList(res.data);
        }  catch (error: any) {
            // Suppress error log if it's due to missing access token (pre-login case)
            if (error?.message === MISSING_TOKEN_ERROR_MSG ) {
                return;
            }

            console.error('Failed to fetch patient list:', error);
        }
    };

    const refreshPatients = async (patientId: number) => {
        try {
            const res = await axiosApexInstance.get(`/task/patient/${patientId}`);
            if (res.data) addPatientRow(res.data);
        } catch (error) {
            console.error('Failed to refresh patient row:', error);
        }
    };

    const addPatientRow = (newPatient: PatientDTO) => {
        setPatientList(prev => [newPatient, ...prev.filter(pt => pt.patientId !== newPatient.patientId)]);
    };

    const getPatientRow = (patientId: number): PatientDTO | null => {
        const match = patientList.find(pt => pt.patientId === patientId);
        return match ?? null;
    };

    return (
        <PatientContext.Provider
            value={{
                selectedPatient,
                setSelectedPatient,
                patientList,
                getNewPatients,
                addPatientRow,
                refreshPatients,
                getPatientRow,
            }}
        >
            {children}
        </PatientContext.Provider>
    );
};

export const usePatient = (): PatientContextType => {
    const context = useContext(PatientContext);
    if (!context) {
        throw new Error('usePatient must be used within a PatientProvider');
    }
    return context;
};
